import { useEffect, useRef, useState } from 'react';
import './tabsBar.css';

import Camp from './searchComponents/camp';
import Backpacking from './searchComponents/backpack';
import Activity from './searchComponents/activity';
import Guide from './searchComponents/guide';

import { ReactComponent as CloseIcon } from '../../assets/icons/trvto_close.svg';

import { useParams } from 'react-router-dom';

import { PlaneIcon, HomeIcon, TentIcon, BookIcon } from '../../assets/icons/searchTabIcons';

export default function TabsBar({ className = '', setIsSearch }) {
    const enableExtraTabs = process.env.REACT_APP_DISABLE_EXTRA_TABS === 'true';

    const { type } = useParams();

    const searchRef = useRef(null);

    // Only real tabs from the URL; when camp/activity/guide are switched off
    // (REACT_APP_DISABLE_EXTRA_TABS=true) always show the trip search.
    const urlTab = !enableExtraTabs && ['trip', 'camp', 'act', 'gu'].includes(type) ? type : 'trip';

    const [activeTab, setActiveTab] = useState(urlTab);

    /* =========================================================
       SYNC ACTIVE TAB WITH URL
    ========================================================= */

    useEffect(() => {
        setActiveTab(urlTab);
    }, [urlTab]);

    /* =========================================================
       CLICK OUTSIDE
    ========================================================= */

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setIsSearch?.(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [setIsSearch]);

    /* =========================================================
       TAB CHANGE
    ========================================================= */

    const handleTabChange = (value) => {
        setActiveTab(value);
    };

    /* =========================================================
       CLOSE
    ========================================================= */

    const handleClose = () => {
        setIsSearch?.(false);
    };

    /* =========================================================
       SEARCH COMPONENT
    ========================================================= */

    const renderSearchComponent = () => {
        switch (activeTab) {
            case 'trip':
                return <Backpacking handleIsSearch={handleClose} type='trip' />;

            case 'camp':
                return <Camp />;

            case 'act':
                return <Activity handleIsSearch={handleClose} type={type || activeTab} />;

            case 'gu':
                return <Guide handleIsSearch={handleClose} type={type || activeTab} />;

            default:
                return <Backpacking handleIsSearch={handleClose} type='trip' />;
        }
    };

    return (
        <div ref={searchRef} className={`homeSearchWrapper ${className} campField`}>
            {/* =====================================================
                DESKTOP SEARCH
            ===================================================== */}

            <div className='desktopSearchCard'>
                {/* =================================================
                    RAISED TAB SECTION
                ================================================= */}

                <div className='tabsWrapper'>
                    {/* TRIP */}

                    <button
                        type='button'
                        className={`searchTab ${activeTab === 'trip' ? 'active' : ''}`}
                        onClick={() => handleTabChange('trip')}
                    >
                        <PlaneIcon />

                        <span>Trip</span>
                    </button>

                    {/* CAMP / VILLA */}

                    <button
                        type='button'
                        disabled={enableExtraTabs}
                        className={`searchTab ${activeTab === 'camp' ? 'active' : ''}`}
                        onClick={() => handleTabChange('camp')}
                    >
                        <HomeIcon />

                        <span>Camp/Villa</span>
                    </button>

                    {/* ACTIVITY */}

                    <button
                        type='button'
                        disabled={enableExtraTabs}
                        className={`searchTab ${activeTab === 'act' ? 'active' : ''}`}
                        onClick={() => handleTabChange('act')}
                    >
                        <TentIcon />

                        <span>Activity</span>
                    </button>

                    {/* GUIDE */}

                    <button
                        type='button'
                        disabled={enableExtraTabs}
                        className={`searchTab ${activeTab === 'gu' ? 'active' : ''}`}
                        onClick={() => handleTabChange('gu')}
                    >
                        <BookIcon />

                        <span>Guide</span>
                    </button>
                </div>

                {/* =================================================
                    SEARCH CONTENT
                ================================================= */}

                <div className='tabContent'>{renderSearchComponent()}</div>
            </div>

            {/* =====================================================
                MOBILE
            ===================================================== */}

            <div className='mobileSearchCard'>
                <div className='mobileSearchTop'>
                    <select
                        value={activeTab}
                        name='type'
                        onChange={(e) => handleTabChange(e.target.value)}
                        className='homeSearch'
                    >
                        <option value='trip'>Trip</option>

                        <option value='camp' disabled={enableExtraTabs}>
                            Camp/Villa
                        </option>

                        <option value='act' disabled={enableExtraTabs}>
                            Activity
                        </option>

                        <option value='gu' disabled={enableExtraTabs}>
                            Guide
                        </option>
                    </select>

                    <button type='button' className='closeTabs' onClick={handleClose}>
                        <CloseIcon />
                    </button>
                </div>

                <div className='mobileTabContent'>{renderSearchComponent()}</div>
            </div>
        </div>
    );
}
